"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { navSections } from '@/lib/navData';

interface SidebarNavProps {
  activeLink?: string;
}

/**
 * Sidebar navigation component with collapsible sections
 */
export default function SidebarNav({ activeLink }: SidebarNavProps) {
  const pathname = usePathname();
  
  // Define "Get Started" zone - pages that should show the focused navigation
  const getStartedZonePages = [
    'overview',
    'quick-start',
    'access-keys',
    'rate-limiting',
    'webhooks', 
    'versioning',
    'formats',
    'pagination',
    'error-handling',
    'tools-libraries',
    'changelog'
  ];

  // Define "Weather Layers" zone - pages that should show the weather data layers navigation
  const weatherLayersZonePages = [
    'core-data-fields',
    'historical-data-fields',
    'weather-codes',
    'data-layers',
    'weather-data-layers'
  ];

  // Define "Weather Forecast" zone - pages that should show the weather forecast & realtime navigation
  const weatherForecastZonePages = [
    'weather-forecast',
    'realtime-weather',
    'weather-timelines',
    'weather-on-routes'
  ];

  // Define "Historical API" zone - pages that should show the historical weather navigation
  const historicalApiZonePages = [
    'recent-history',
    'historical-weather',
    'climate-normals',
    'historical-climate-normals'
  ];

  // Define "Monitoring" zone - pages that should show the Tomorrow.io API and Platform Data Integration navigation
  const monitoringZonePages = [
    'locations-api',
    'insights-api',
    'alerts-api',
    'events-api',
    'custom-events-api',
    'on-demand-events-api',
    'severe-weather-events',
    'notifications-api',
    'flight-schedule',
    'stations-data',
    'scheduled-events',
    'platform-integration',
    'platform-data-integration'
  ];

  // Define "Weather Map" zone - pages that should show the weather maps navigation
  const weatherMapZonePages = [
    'weather-maps'
  ];

  // Check if we're in the "Get Started" zone
  const isInGetStartedZone = React.useMemo(() => {
    // Check by activeLink
    if (getStartedZonePages.includes(activeLink || '')) {
      return true;
    }
    
    // Check by pathname for additional coverage
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.includes('overview') || 
        pathSegments.includes('getting-started') ||
        pathname === '/error-handling') {
      return true;
    }
    
    return false;
  }, [activeLink, pathname]);

  // Check if we're in the "Weather Layers" zone
  const isInWeatherLayersZone = React.useMemo(() => {
    // Check by activeLink
    if (weatherLayersZonePages.includes(activeLink || '')) {
      return true;
    }
    
    // Check by pathname for additional coverage
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.includes('data-layers') || 
        pathSegments.includes('historical-data-fields')) {
      return true;
    }
    
    return false;
  }, [activeLink, pathname]);

  // Check if we're in the "Weather Forecast" zone
  const isInWeatherForecastZone = React.useMemo(() => {
    // Check by activeLink
    if (weatherForecastZonePages.includes(activeLink || '')) {
      return true;
    }
    
    // Check by pathname for additional coverage
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.includes('weather-forecast') || 
        pathSegments.includes('realtime-weather') ||
        pathSegments.includes('weather-timelines') ||
        pathSegments.includes('weather-on-routes')) {
      return true;
    }
    
    return false;
  }, [activeLink, pathname]);

  // Check if we're in the "Historical API" zone
  const isInHistoricalApiZone = React.useMemo(() => {
    // Check by activeLink
    if (historicalApiZonePages.includes(activeLink || '')) {
      return true;
    }
    
    // Check by pathname for additional coverage
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.includes('recent-history') || 
        pathSegments.includes('historical-weather') ||
        pathSegments.includes('historical-climate-normals')) {
      return true;
    }
    
    return false;
  }, [activeLink, pathname]);

  // Check if we're in the "Monitoring" zone
  const isInMonitoringZone = React.useMemo(() => {
    // Check by activeLink
    if (monitoringZonePages.includes(activeLink || '')) {
      return true;
    }
    
    // Check by pathname for additional coverage
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.includes('locations-api') || 
        pathSegments.includes('insights-api') ||
        pathSegments.includes('alerts-api') ||
        pathSegments.includes('custom-events-api') ||
        pathSegments.includes('on-demand-events-api') ||
        pathSegments.includes('severe-weather-events') ||
        pathSegments.includes('integration')) {
      return true;
    }
    
    return false;
  }, [activeLink, pathname]);

  // Check if we're in the "Weather Map" zone
  const isInWeatherMapZone = React.useMemo(() => {
    // Check by activeLink
    if (weatherMapZonePages.includes(activeLink || '')) {
      return true;
    }
    
    // Check by pathname for additional coverage
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.includes('weather-maps')) {
      return true;
    }
    
    return false;
  }, [activeLink, pathname]);

  // Filter sections based on zone
  const filteredSections = React.useMemo(() => {
    if (isInGetStartedZone) {
      // For "Get Started" zone, show only Overview and API Fundamentals
      return navSections.filter(section => 
        section.id === 'overview' || section.id === 'api-fundamentals'
      );
    }
    if (isInWeatherLayersZone) {
      // For "Weather Layers" zone, show only Weather Data Layers
      return navSections.filter(section => 
        section.id === 'weather-data-layers'
      );
    }
    if (isInWeatherForecastZone) {
      // For "Weather Forecast" zone, show only Weather Forecast & Realtime
      return navSections.filter(section => 
        section.id === 'weather-forecast-realtime'
      );
    }
    if (isInHistoricalApiZone) {
      // For "Historical API" zone, show only Historical Weather
      return navSections.filter(section => 
        section.id === 'historical-weather'
      );
    }
    if (isInMonitoringZone) {
      // For "Monitoring" zone, show Tomorrow.io API and Platform Data Integration
      return navSections.filter(section => 
        section.id === 'tomorrow-io-api' || section.id === 'platform-data-integration'
      );
    }
    if (isInWeatherMapZone) {
      // For "Weather Map" zone, show only Weather Maps
      return navSections.filter(section => 
        section.id === 'weather-maps'
      );
    }
    // For all other pages, show all sections
    return navSections;
  }, [isInGetStartedZone, isInWeatherLayersZone, isInWeatherForecastZone, isInHistoricalApiZone, isInMonitoringZone, isInWeatherMapZone]);
  
  // Keep all accordions open all the time
  const expandedSections = React.useMemo(() => {
    const allSections = new Set<string>();
    filteredSections.forEach(section => {
      if (section.id !== 'overview') {
        allSections.add(section.id);
      }
    });
    return allSections;
  }, [filteredSections]);

  // No toggle functionality - accordions stay open
  const toggleSection = (sectionId: string) => {
    // Do nothing - accordions stay open
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname === href || activeLink === href;
  };

  const renderBadge = (item: any) => {
    if (item.isComingSoon) {
      return <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Soon</span>;
    }
    if (item.isPremium) {
      return <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Premium</span>;
    }
    if (item.isExperimental) {
      return <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Beta</span>;
    }
    if (item.isDeprecated) {
      return <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Deprecated</span>;
    }
    return null;
  };

  const renderMethodBadge = (method?: string) => {
    if (!method) return null;
    
    const colors = {
      GET: 'bg-green-100 text-green-800',
      POST: 'bg-blue-100 text-blue-800',
      PUT: 'bg-orange-100 text-orange-800',
      DELETE: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`text-xs px-2 py-1 rounded font-mono ${colors[method as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {method}
      </span>
    );
  };

  return (
    <nav className="styled-scrollbar flex h-[calc(100vh-160px)] flex-col overflow-y-scroll pb-4 pr-2 dark:text-white">
      <ul className="last-of-type:pb-3">
        {filteredSections.map((section) => (
          <li key={section.id} className="my-1.5 ml-[3px]" data-active={section.items.some(item => isActive(item.href))}>
            {/* Special handling for Overview - standalone item */}
            {section.id === 'overview' ? (
              <Link
                href={section.items[0].href}
                className={`hover:text-gray-1000 relative flex w-full cursor-pointer items-center justify-between rounded-md py-1 pl-2 text-left text-sm font-medium ${
                  isActive(section.items[0].href)
                    ? 'text-gray-1000'
                    : 'text-gray-1000'
                }`}
              >
                {section.title}
              </Link>
            ) : (
              <>
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="hover:text-gray-1000 relative flex w-full cursor-pointer items-center justify-between rounded-md py-1 pl-2 text-left text-sm text-gray-1000 font-medium"
                >
                  {section.title}
                  {section.items.length > 1 && (
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        expandedSections.has(section.id) ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>

                {/* Collapsible Section Content */}
                <div 
                  data-is-collapsed={!expandedSections.has(section.id)}
                  className={`overflow-hidden grid transition-all duration-200 ease-in-out ${
                    expandedSections.has(section.id) 
                      ? 'opacity-1 grid-rows-[1fr]' 
                      : 'opacity-0 grid-rows-[0fr]'
                  }`}
                  style={{
                    opacity: expandedSections.has(section.id) ? 1 : 0,
                    gridTemplateRows: expandedSections.has(section.id) ? '1fr' : '0fr',
                    transition: 'opacity 0.15s, grid-template-rows 0.2s ease-in-out'
                  }}
                >
                  <div className="relative overflow-hidden">
                    <ul className="px-0.5 last-of-type:mb-0 mb-8">
                      {section.items.map((item) => (
                        <li key={item.id} className="my-1.5" data-active={isActive(item.href)}>
                          <Link
                            href={item.href}
                            className={`hover:text-gray-1000 relative flex w-full cursor-pointer items-center justify-between rounded-md py-1 pl-4 text-left text-sm ${
                              isActive(item.href)
                                ? 'text-gray-1000 font-bold'
                                : 'text-gray-900'
                            }`}
                          >
                            <span className="flex-1 truncate">{item.title}</span>
                            <div className="flex items-center gap-2 ml-2">
                              {renderMethodBadge(item.method)}
                              {renderBadge(item)}
                            </div>
                          </Link>
                          {/* Render subitems if they exist */}
                          {item.subitems && item.subitems.length > 0 && (
                            <ul className="ml-4 mt-1 space-y-1">
                              {item.subitems.map((subitem) => (
                                <li key={subitem.id} className="my-1" data-active={isActive(subitem.href)}>
                                  <Link
                                    href={subitem.href}
                                    className={`hover:text-gray-1000 relative flex w-full cursor-pointer items-center justify-between rounded-md py-1 pl-4 text-left text-xs ${
                                      isActive(subitem.href)
                                        ? 'text-gray-1000 font-bold'
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    <span className="flex-1 truncate">{subitem.title}</span>
                                    <div className="flex items-center gap-2 ml-2">
                                      {renderMethodBadge(subitem.method)}
                                      {renderBadge(subitem)}
                                    </div>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                    {/* Gradient fade at bottom */}
                    <div aria-hidden="true" className="from-gray-0 pointer-events-none absolute bottom-0 left-0 z-10 h-[6px] w-full bg-gradient-to-t"></div>
                  </div>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}